<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { EinspeisungBiogas, EinspeisungBiogasCollection } from '@/models/EinspeisungBiogas';
import KpJahrSelect from '@/components/KpJahrSelect.vue';
import KpMandantKantonSelect from '@/components/KpMandantKantonSelect.vue';
import { FilterMatchMode } from '@primevue/core/api';
import type { DataTableFilterEvent } from 'primevue/datatable';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import { instance as api } from '@/helpers/api';
import KpGemeindeSelect from '@/components/KpGemeindeSelect.vue';
import CopyJahrDialog from '@/components/CopyJahrDialog.vue';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'kanton_kuerzel' },
    { direction: 'asc', property: 'gemeinde' },
    { direction: 'asc', property: 'jahr' },
]);
const expandedRowGroups: Ref<string[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-expandedRowGroups`, []);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new EinspeisungBiogasCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(async () => {
    if (collection.loading.value !== true) {
        await collection.query();
    }
    loadeinspeisungTotal();
});

function editRecord(r: EinspeisungBiogas) {
    router.push({ name: 'bg_jw_einspeisung_biogas_edit', params: { id: r?.id } });
}

async function deleteRecord(r: EinspeisungBiogas) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_jw_einspeisung_biogas_edit', params: { id: 'new' } });
}

type Filter = {
    property: string;
    value: any;
    id: string;
};
const filters = ref({
    jahr: { value: null, matchMode: FilterMatchMode.EQUALS },
    kanton_id: { value: null, matchMode: FilterMatchMode.EQUALS },
    gemeinde_id: { value: null, matchMode: FilterMatchMode.EQUALS },
});

const filter: Ref<Filter[]> = ref([]);
function onFilterChange(filterEvent: DataTableFilterEvent) {
    const prop = 'value';
    filter.value = [];
    for (const key in filterEvent.filters) {
        // @ts-expect-error it's save, do it!
        const value = filterEvent.filters[key].hasOwnProperty(prop) ? filterEvent.filters[key][prop] : null;
        filter.value.push({
            property: key,
            id: key,
            value,
        });
    }
    collection.setQueryParams({ filter: filter.value });
    collection.query();
    loadeinspeisungTotal();
}

const einspeisungTotal = ref('');
async function loadeinspeisungTotal() {
    const res = await api().post('/EinspeisungBiogas/getEinspeisungTotal', {
        filter: filter.value,
    });
    if (res && res.data && res.data.total) {
        einspeisungTotal.value = res.data.total;
    } else {
        einspeisungTotal.value = '0';
    }
}

const showCopyDlg = ref(false);
function openCopyDialog() {
    showCopyDlg.value = true;
}

async function copy(targetJahr: number, kantonId: number, sourceJahr: number | null) {
    showCopyDlg.value = false;
    try {
        await api().post('/EinspeisungBiogas/copy', {
            targetJahr,
            kantonId,
            sourceJahr,
        });
        toast.add({
            severity: 'success',
            summary: getI18n().t('erfolg'),
            detail: getI18n().t('eintraege_kopiert'),
        });
        await reloadCollection();
        loadeinspeisungTotal();
    } catch (e) {
        errToToast(e, toast);
    }
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:filters="filters"
            v-model:table-sort="tableSort"
            v-model:expandedRowGroups="expandedRowGroups"
            :title="$t('einspeisung_biogas')"
            scrollable
            scroll-height="flex"
            paginator
            filterDisplay="row"
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            showCopyBtn
            @copy="openCopyDialog()"
            @editrecord="editRecord($event as EinspeisungBiogas)"
            @deleterecord="deleteRecord($event as EinspeisungBiogas)"
            @new="newRecord"
            @filter="onFilterChange"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column
                field="kanton_id"
                sortable
                :header="$t('kanton')"
                sort-field="kanton_kuerzel"
                :showFilterMenu="false"
            >
                <template #body="slotProps">{{ slotProps.data.kanton_kuerzel }}</template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpMandantKantonSelect
                        v-model="filterModel.value"
                        fluid
                        @change="filterCallback()"
                    ></KpMandantKantonSelect>
                </template>
            </Column>
            <Column
                field="gemeinde_id"
                sortable
                :header="$t('gemeinde')"
                sort-field="gemeinde"
                :show-filter-menu="false"
            >
                <template #body="slotProps">{{ slotProps.data.gemeinde }}</template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpGemeindeSelect
                        :mandant="true"
                        :label="null"
                        fluid
                        v-model="filterModel.value"
                        @change="filterCallback()"
                    ></KpGemeindeSelect>
                </template>
            </Column>
            <Column field="jahr" :header="$t('jahr')" filter-field="jahr" :showFilterMenu="false">
                <template #body="slotProps">
                    <div>{{ slotProps.data.jahr }}</div>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpJahrSelect fluid v-model="filterModel.value" @change="filterCallback()"></KpJahrSelect>
                </template>
            </Column>
            <Column field="einspeisung" :header="$t('einspeisung')"></Column>
            <ColumnGroup type="footer">
                <Row>
                    <Column footer="Total:" :colspan="4" footerStyle="text-align:right" />
                    <Column :footer="einspeisungTotal" />
                    <Column />
                </Row>
            </ColumnGroup>
        </KpBaseCollectionGrid>
        <CopyJahrDialog v-model="showCopyDlg" with-from-year @copy="copy"></CopyJahrDialog>
    </div>
</template>
